/* eslint-disable react/no-unescaped-entities */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LmsShell, lmsApi, getStudentToken, clearStudentSession } from "./shell";
import { PracticeBanner } from "./PracticeBanner";
import { ArrowLeft, LogOut, Send, RotateCcw, Check, X } from "lucide-react";

/**
 * Module quiz engine.
 * Route: /dashboard/modules/:moduleSlug/quiz
 */
export default function QuizEngine() {
  const { moduleSlug } = useParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | running | results | error
  const [module, setModule] = useState(null);
  const [attempt, setAttempt] = useState(null); // { attempt_id, questions[], pass_pct, warning }
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!getStudentToken()) { nav("/login", { replace: true }); return; }
    (async () => {
      try {
        const prog = await lmsApi.progress();
        const cid = (prog && prog[0] && prog[0].course_id) || "advanced-rpas-2026";
        const course = await lmsApi.courseFull(cid);
        const mod = course.modules.find((m) => m.slug === moduleSlug);
        if (!mod) throw new Error("Module not found");
        setModule(mod);
        const started = await lmsApi.startQuiz(mod.id);
        setAttempt(started);
        setPhase("running");
      } catch (e) {
        if (e.status === 401) { clearStudentSession(); nav("/login", { replace: true }); return; }
        setErr(e.message || "Failed to start quiz");
        setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleSlug]);

  const signOut = () => { clearStudentSession(); nav("/login", { replace: true }); };
  const rightSlot = (
    <button onClick={signOut} className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="quiz-signout">
      <LogOut size={12} /> Sign out
    </button>
  );

  const selectAnswer = (qid, idx) => setAnswers((a) => ({ ...a, [qid]: idx }));

  const submit = async () => {
    try {
      const r = await lmsApi.submitQuiz(module.id, attempt.attempt_id, answers);
      setResult(r);
      setPhase("results");
    } catch (e) { setErr(e.message || "Submit failed"); }
  };

  const retake = async () => {
    setAnswers({}); setResult(null); setErr("");
    try {
      const started = await lmsApi.startQuiz(module.id);
      setAttempt(started); setPhase("running");
    } catch (e) { setErr(e.message || "Failed to restart"); setPhase("error"); }
  };

  const allAnswered = attempt && attempt.questions.every((q) => answers[q.id] !== undefined);

  return (
    <LmsShell subtitle={module ? `Module ${module.code} · Quiz` : "Quiz"} rightSlot={rightSlot} wide>
      <div className="tbg-lms-wide" data-testid="quiz-engine">
        <PracticeBanner />

        <div style={{ marginBottom: 14 }}>
          <Link to={module ? `/dashboard/modules/${module.slug}` : "/dashboard"} className="tbg-btn ghost" style={{ padding: "6px 12px" }} data-testid="quiz-back">
            <ArrowLeft size={12} /> Back to module
          </Link>
        </div>

        {phase === "loading" && <div className="tbg-panel">Loading quiz…</div>}

        {phase === "error" && (
          <div className="tbg-panel" data-testid="quiz-error">
            <div className="tbg-lms-err">{err}</div>
            <Link to="/dashboard" className="tbg-btn" style={{ marginTop: 12 }}>Back to dashboard</Link>
          </div>
        )}

        {phase === "running" && attempt && (
          <>
            <div className="tbg-panel" style={{ marginBottom: 16 }}>
              <div className="tbg-section-sub">Module Quiz</div>
              <h1 className="tbg-section-title">{module?.title}</h1>
              <div style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.08em", marginTop: 4 }}>
                {attempt.questions.length} questions · Pass mark {attempt.pass_pct}%
              </div>
              {attempt.warning && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(240,190,60,0.08)",
                  border: "1px solid rgba(240,190,60,0.35)", color: "#f0be3c", borderRadius: 4, fontSize: 12 }}>
                  {attempt.warning}
                </div>
              )}
            </div>

            {attempt.questions.map((q, i) => (
              <QuizQuestion
                key={q.id} q={q} index={i}
                selected={answers[q.id]}
                onSelect={(idx) => selectAnswer(q.id, idx)}
              />
            ))}

            <div className="tbg-actionbar">
              <div style={{ font: "500 11px 'JetBrains Mono', monospace", color: "#7b8b87", letterSpacing: "0.06em" }}>
                Answered: {Object.keys(answers).length} / {attempt.questions.length}
              </div>
              <button className="tbg-btn" onClick={submit} disabled={!allAnswered} data-testid="quiz-submit">
                <Send size={13} /> Submit quiz
              </button>
            </div>

            {err && <div className="tbg-lms-err" style={{ marginTop: 10 }}>{err}</div>}
          </>
        )}

        {phase === "results" && result && (
          <QuizResults
            result={result} module={module} onRetake={retake}
            onBack={() => nav(`/dashboard/modules/${module.slug}`)}
          />
        )}
      </div>
    </LmsShell>
  );
}

function QuizQuestion({ q, index, selected, onSelect, reviewMode, correctIndex, rationale }) {
  return (
    <div className="tbg-qcard" data-testid={`quiz-q-${index + 1}`}>
      <div className="tbg-qnum">Question {index + 1}</div>
      <p className="tbg-qtext">
        {q.is_placeholder && <span className="tbg-placeholder-tag" title="Placeholder — awaiting licensed content">SAMPLE</span>}
        {q.prompt}
      </p>
      {q.options.map((opt, idx) => {
        let cls = "tbg-qopt";
        if (reviewMode) {
          if (idx === correctIndex) cls += " correct";
          else if (idx === selected && idx !== correctIndex) cls += " wrong";
        } else if (selected === idx) cls += " selected";
        return (
          <label key={idx} className={cls} data-testid={`quiz-q-${index + 1}-opt-${idx}`}>
            <input
              type="radio"
              name={`q-${q.id}`}
              disabled={!!reviewMode}
              checked={selected === idx}
              onChange={() => onSelect && onSelect(idx)}
            />
            {opt}
          </label>
        );
      })}
      {reviewMode && rationale && (
        <div className="tbg-rationale" data-testid={`quiz-q-${index + 1}-rationale`}>
          <strong style={{ color: "#3dc0af", letterSpacing: "0.06em", fontSize: 11, textTransform: "uppercase" }}>Why: </strong>
          {rationale}
        </div>
      )}
    </div>
  );
}

function QuizResults({ result, module, onRetake, onBack }) {
  const passed = result.passed;
  return (
    <div data-testid="quiz-results">
      <div className="tbg-score" style={{ marginBottom: 20 }}>
        <div className={`pct ${passed ? "" : "fail"}`} data-testid="quiz-score-pct">{result.score_pct}%</div>
        <div className="lbl">{passed ? "Passed" : "Not yet"}</div>
        <div className="msg">
          {result.correct} of {result.total} correct · Pass mark {result.pass_pct}%
        </div>
        <div className="msg" style={{ maxWidth: 460 }}>
          {passed
            ? "Solid work. Review the answers below, then continue to the next module or take the practice exam."
            : `Retake as many times as you need — repeated exposure is the point. Focus on rationales for the ${result.total - result.correct} you missed.`}
        </div>
      </div>

      <div className="tbg-section-sub">Review</div>
      {result.results.map((r, i) => (
        <QuizQuestion
          key={r.id} index={i}
          q={{ id: r.id, prompt: r.prompt, options: r.options }}
          selected={r.your_index} reviewMode correctIndex={r.correct_index} rationale={r.rationale}
        />
      ))}

      <div className="tbg-actionbar">
        <button className="tbg-btn ghost" onClick={onBack} data-testid="quiz-results-back">
          Back to module
        </button>
        <button className="tbg-btn" onClick={onRetake} data-testid="quiz-retake">
          <RotateCcw size={13} /> Retake quiz
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#3dc0af", fontSize: 11 }}>
          <Check size={12} /> Correct
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#ef6a6a", fontSize: 11 }}>
          <X size={12} /> Missed
        </span>
      </div>
    </div>
  );
}
